/**
 * Fixed-position coordinates for a portaled popover, derived from its
 * trigger's bounding rect. Shared by `SearchableSelect` and
 * `SearchableMultiSelect` (#10298).
 *
 * It lives in its own module because those two components now live in two
 * files (#13473, `check:file-size-ratchet`), and a shared type imported from
 * either one of them would make the pair mutually dependent for no reason.
 *
 * Collision-aware since #16177: a trigger near the bottom of a short viewport
 * (the meeting create bar's captain picker, inside an `h-dvh overflow-hidden`
 * room frame) opened its menu past the bottom edge, where nothing scrolls.
 * The menu now flips above the trigger when below is too short and above has
 * more room, and its height is clamped to the side it opens on so the option
 * list scrolls internally instead.
 */

/** Space between the trigger and the popover. */
export const MENU_GAP = 4
/** Space kept clear between the popover and the viewport edge. */
export const VIEWPORT_MARGIN = 8
/**
 * Height the popover wants: search header (~52px) + `max-h-60` list (240px)
 * + room for a footer row. Below that much space below the trigger, the
 * larger side wins.
 */
export const MENU_PREFERRED_HEIGHT = 320

export type MenuPlacement = 'below' | 'above'

/**
 * Exactly one of `top` / `bottom` is set. An `above` menu is anchored by its
 * BOTTOM edge to the trigger's top so its (unknown) rendered height grows
 * upward, away from the trigger.
 */
export type MenuPos = {
  placement: MenuPlacement
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

type TriggerRect = Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>

/**
 * Pure placement decision for the popover. `viewportHeight` is the height
 * `position: fixed` coordinates resolve against (`window.innerHeight`).
 */
export function computeMenuPos(
  rect: TriggerRect,
  viewportHeight: number,
): MenuPos {
  const spaceBelow = viewportHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN
  const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN
  const flip = spaceBelow < MENU_PREFERRED_HEIGHT && spaceAbove > spaceBelow
  const base = { left: rect.left, width: rect.width }
  // The clamp is the side's space and nothing more: there is no height floor,
  // because a floor larger than the side's space is exactly what pushes the
  // menu's far edge off screen (review of #16177). The side chosen is already
  // the larger one, so a tiny clamp only happens when neither side has room.
  if (flip) {
    return {
      ...base,
      placement: 'above',
      bottom: viewportHeight - rect.top + MENU_GAP,
      maxHeight: Math.max(spaceAbove, 0),
    }
  }
  return {
    ...base,
    placement: 'below',
    top: rect.bottom + MENU_GAP,
    maxHeight: Math.max(spaceBelow, 0),
  }
}

/** Measures the trigger and the viewport; the DOM-reading half. */
export function measureMenuPos(el: HTMLElement): MenuPos {
  return computeMenuPos(el.getBoundingClientRect(), window.innerHeight)
}
